import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Check, Play, Zap, X, ShieldAlert, GitBranch,
    ZoomIn, ZoomOut, Maximize2, Save, Edit3, Trash2
} from "lucide-react";

export default function WorkflowsBuilder() {
    const [searchParams] = useSearchParams();
    const queryId = searchParams.get("id");
    const [workflows, setWorkflows] = useState([]);
    const [activeWorkflowId, setActiveWorkflowId] = useState(queryId || "wf-fortigate-perimeter-containment");
    const [loading, setLoading] = useState(true);
    const [zoom, setZoom] = useState(1);
    const [toast, setToast] = useState(null);
    const [runModalOpen, setRunModalOpen] = useState(false);
    const [simulationRunning, setSimulationRunning] = useState(false);
    const [simulationResults, setSimulationResults] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchWorkflows();
    }, [queryId]);

    const fetchWorkflows = async () => {
        setLoading(true);
        try {
            const currentToken = localStorage.getItem("token") || token;
            const res = await axios.get(`${backendURL}/api/respond/workflows`, {
                headers: { Authorization: `Bearer ${currentToken}` }
            });
            const list = res.data.workflows || [];
            setWorkflows(list);
            
            const targetId = queryId || activeWorkflowId;
            if (list.length > 0 && list.find(w => w.id === targetId)) {
                setActiveWorkflowId(targetId);
            } else if (list.length > 0) {
                setActiveWorkflowId(list[0].id);
            }
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load workflows:", err);
        }
        setLoading(false);
    };

    const currentWorkflow = workflows.find(w => w.id === activeWorkflowId) || workflows[0];

    const handleDeleteWorkflow = async (e, wfId) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        const targetId = wfId || currentWorkflow?.id || (workflows.length > 0 ? workflows[0].id : null);
        if (!targetId) {
            setToast("No workflow selected to delete");
            setTimeout(() => setToast(null), 2500);
            return;
        }

        const targetWf = workflows.find(w => w.id === targetId);
        const wfName = targetWf?.name || targetId;
        const confirmed = window.confirm(`Are you sure you want to permanently delete the workflow playbook "${wfName}"?`);
        if (!confirmed) return;

        try {
            const currentToken = localStorage.getItem("token") || token;
            await axios.delete(`${backendURL}/api/respond/workflows/${targetId}`, {
                headers: { Authorization: `Bearer ${currentToken}` }
            });
            const remaining = workflows.filter(w => w.id !== targetId);
            setWorkflows(remaining);
            if (activeWorkflowId === targetId) {
                setActiveWorkflowId(remaining[0]?.id || "");
            }
            setToast(`Workflow "${wfName}" deleted successfully`);
            setTimeout(() => setToast(null), 3000);
        } catch (err) {
            console.error("Failed to delete workflow:", err);
            setToast("Failed to delete workflow: " + (err.response?.data?.error || err.message));
            setTimeout(() => setToast(null), 3500);
        }
    };

    const handleToggleWorkflow = async () => {
        if (!currentWorkflow) return;
        const nextState = !currentWorkflow.enabled;
        try {
            await axios.post(`${backendURL}/api/respond/workflows/${currentWorkflow.id}/toggle`, { enabled: nextState }, { headers });
            setWorkflows(prev => prev.map(w => w.id === currentWorkflow.id ? { ...w, enabled: nextState } : w));
            setToast(`Workflow ${nextState ? "Enabled" : "Disabled"}`);
            setTimeout(() => setToast(null), 2500);
        } catch (err) {
            console.error("Failed to toggle workflow:", err);
        }
    };

    const handleRunSimulation = async () => {
        if (!currentWorkflow) return;
        setRunModalOpen(true);
        setSimulationRunning(true);
        setSimulationResults(null);
        try {
            const res = await axios.post(`${backendURL}/api/respond/workflows/${currentWorkflow.id}/run`, {}, { headers });
            setTimeout(() => {
                setSimulationResults(res.data);
                setSimulationRunning(false);
            }, 1200);
        } catch (err) {
            console.error("Failed to run workflow:", err);
            setSimulationRunning(false);
        }
    };

    const getNodeColor = (color) => {
        switch (color) {
            case "emerald":
                return "border-emerald-500/40 bg-emerald-950/20 text-emerald-400";
            case "blue":
                return "border-blue-500/40 bg-blue-950/20 text-blue-400";
            case "rose":
                return "border-rose-500/40 bg-rose-950/20 text-rose-400";
            case "purple":
                return "border-purple-500/40 bg-purple-950/20 text-purple-400";
            default:
                return "border-white/[0.1] bg-black/40 text-slate-300";
        }
    };

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>03</span>
                <span>/</span>
                <span className="text-slate-400">SOAR Response Workflows</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (4.5 cols) ═══ */}
                <div className="lg:col-span-4 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Automate the repeatable response.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            Codify end-to-end zero-trust containment workflows across <strong>Microsoft Defender XDR</strong>, <strong>ThreatDown EDR</strong>, <strong>FortiGate edge firewalls</strong>, the <strong>Approvals Queue</strong>, and <strong>Zoho Desk</strong> the moment a high-risk security alert triggers.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Cross-platform orchestration across Defender XDR, ThreatDown EDR & FortiGate.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Automated rule staging into the Firewall Approvals Queue.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Automated incident ticketing via Zoho Desk & audit logging.</span>
                        </div>
                    </div>

                    {/* Workflow Template Selector */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[12px] font-bold text-white uppercase tracking-wider block">
                                Enterprise Playbook Templates
                            </span>
                            <span className="text-[10px] font-mono text-slate-400 bg-white/[0.04] px-2 py-0.5 rounded">
                                {workflows.length} Playbooks
                            </span>
                        </div>
                        <div className="space-y-2">
                            {workflows.map((wf) => (
                                <div
                                    key={wf.id}
                                    onClick={() => setActiveWorkflowId(wf.id)}
                                    className={`w-full p-3 rounded-xl border text-left transition-all cursor-pointer group relative ${
                                        activeWorkflowId === wf.id
                                            ? "bg-blue-600/15 border-blue-500/40 text-white"
                                            : "bg-black/20 border-white/[0.04] text-slate-400 hover:text-slate-200 hover:bg-white/[0.02]"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-bold text-[12.5px] truncate max-w-[170px]">{wf.name}</span>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-mono text-emerald-400">{wf.totalRuns || 0} runs</span>
                                            <button
                                                type="button"
                                                onClick={(e) => handleDeleteWorkflow(e, wf.id)}
                                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-rose-600 text-rose-400 hover:text-white rounded transition-all"
                                                title="Delete workflow"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-slate-400 mt-1 line-clamp-1">{wf.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: Visual Flowchart Canvas (7.5 cols) ═══ */}
                <div className="lg:col-span-8 bg-[#111827] border border-white/[0.06] rounded-2xl shadow-2xl overflow-hidden flex flex-col min-h-[680px]">
                    
                    {/* Top Canvas Bar matching screenshot 03 */}
                    <div className="p-4 border-b border-white/[0.06] bg-black/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        {/* Title & Enable Switch */}
                        <div className="flex items-center gap-3">
                            <span className="text-[11px] font-mono text-slate-400 uppercase font-bold">QueryTel</span>
                            <span className="text-slate-600">/</span>
                            <div className="flex items-center gap-2.5">
                                <span className="text-[13px] font-bold text-white">
                                    {currentWorkflow?.name}
                                </span>
                                <button
                                    onClick={handleToggleWorkflow}
                                    className={`w-9 h-5 rounded-full transition-colors relative p-0.5 focus:outline-none ${
                                        currentWorkflow?.enabled ? "bg-purple-600" : "bg-slate-700"
                                    }`}
                                >
                                    <div
                                        className={`w-4 h-4 rounded-full bg-white transition-transform ${
                                            currentWorkflow?.enabled ? "translate-x-4" : "translate-x-0"
                                        }`}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Top Right Action Buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRunSimulation}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg text-[11.5px] font-bold shadow-md transition-all"
                            >
                                <Play className="w-3.5 h-3.5 fill-current" />
                                Run / Test
                            </button>
                            <Link
                                to="/workflows/designer"
                                className="flex items-center gap-1 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600 text-purple-300 hover:text-white rounded-lg text-[11.5px] font-bold border border-purple-500/30 transition-all"
                            >
                                <Edit3 className="w-3 h-3" />
                                Open Designer
                            </Link>
                            <button
                                onClick={(e) => handleDeleteWorkflow(e, currentWorkflow?.id)}
                                className="px-2.5 py-1.5 bg-rose-600/20 hover:bg-rose-600 text-rose-300 hover:text-white rounded-lg text-[11.5px] font-medium border border-rose-500/30 transition-all flex items-center gap-1"
                                title="Delete this workflow"
                            >
                                <Trash2 className="w-3 h-3" />
                                Delete
                            </button>
                        </div>
                    </div>

                    {/* Canvas Area with Dot Pattern */}
                    <div className="flex-1 relative bg-[#0B0F17] p-8 overflow-auto custom-scrollbar flex items-center justify-center min-h-[540px]">
                        
                        {/* Background Grid Dots */}
                        <div
                            className="absolute inset-0 opacity-20 pointer-events-none"
                            style={{
                                backgroundImage: "radial-gradient(#94A3B8 1px, transparent 1px)",
                                backgroundSize: "20px 20px"
                            }}
                        />

                        {/* Visual Nodes Hierarchy */}
                        <div
                            className="space-y-6 max-w-lg w-full transition-transform duration-150 relative z-10"
                            style={{ transform: `scale(${zoom})`, transformOrigin: "center top" }}
                        >
                            {(currentWorkflow?.nodes || []).map((node, nIdx) => {
                                const isTrigger = node.type === "trigger";
                                const isCondition = node.type === "condition";
                                const colorClass = getNodeColor(node.color);

                                return (
                                    <div key={node.id} className="relative group flex flex-col items-center">
                                        
                                        {/* Connector Arrow from previous node */}
                                        {nIdx > 0 && (
                                            <div className="w-0.5 h-6 bg-gradient-to-b from-blue-500/40 to-indigo-500/60 my-[-2px] relative flex items-center justify-center">
                                                <div className="w-2 h-2 rounded-full bg-blue-400 animate-ping opacity-75" />
                                            </div>
                                        )}

                                        {/* Node Box */}
                                        <div
                                            onClick={() => setSelectedNode(node)}
                                            className={`w-full p-4 rounded-2xl border ${colorClass} bg-[#0F172A]/90 backdrop-blur-md shadow-xl hover:border-white/40 cursor-pointer transition-all hover:scale-[1.02]`}
                                        >
                                            <div className="flex items-center justify-between mb-1.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-lg bg-black/40 flex items-center justify-center font-bold text-[11px]">
                                                        {isTrigger && <Zap className="w-3.5 h-3.5 text-emerald-400" />}
                                                        {node.type === "action" && <ShieldAlert className="w-3.5 h-3.5 text-blue-400" />}
                                                        {isCondition && <GitBranch className="w-3.5 h-3.5 text-rose-400" />}
                                                    </div>
                                                    <h4 className="text-[12.5px] font-bold text-white tracking-tight">
                                                        {node.title}
                                                    </h4>
                                                </div>

                                                {node.connector && (
                                                    <span className="text-[9px] font-mono uppercase bg-white/[0.04] text-slate-300 px-2 py-0.5 rounded border border-white/[0.06]">
                                                        {node.connector}
                                                    </span>
                                                )}
                                            </div>

                                            <p className="text-[11px] text-slate-300 pl-8 leading-relaxed">
                                                {node.description}
                                            </p>

                                            {/* Branch Badges if Condition */}
                                            {isCondition && (
                                                <div className="mt-3 pl-8 flex flex-wrap items-center gap-2 text-[10px] font-mono">
                                                    {(node.branches && node.branches.length > 0) ? (
                                                        node.branches.map((b, bIdx) => (
                                                            <span
                                                                key={bIdx}
                                                                className={`px-2 py-0.5 rounded border ${
                                                                    bIdx === 0
                                                                        ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 font-bold"
                                                                        : "bg-slate-800/80 text-slate-400 border-white/[0.06]"
                                                                }`}
                                                            >
                                                                {b.startsWith("Branch:") || b.startsWith("False:") || b.startsWith("✓") ? b : `✓ ${b}`}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold">
                                                            ✓ Condition Evaluated
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                );
                            })}
                        </div>

                        {/* Bottom-Left Zoom Controls */}
                        <div className="absolute bottom-4 left-4 z-20 flex items-center gap-1 bg-[#111827] border border-white/[0.08] rounded-xl p-1 shadow-lg text-slate-300 text-[11px]">
                            <button
                                onClick={() => setZoom(prev => Math.max(0.7, prev - 0.1))}
                                className="p-1.5 hover:bg-white/[0.08] rounded-lg"
                                title="Zoom Out"
                            >
                                <ZoomOut className="w-3.5 h-3.5" />
                            </button>
                            <span className="px-2 font-mono">{Math.round(zoom * 100)}%</span>
                            <button
                                onClick={() => setZoom(prev => Math.min(1.3, prev + 0.1))}
                                className="p-1.5 hover:bg-white/[0.08] rounded-lg"
                                title="Zoom In"
                            >
                                <ZoomIn className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={() => setZoom(1)}
                                className="p-1.5 hover:bg-white/[0.08] rounded-lg"
                                title="Reset Zoom"
                            >
                                <Maximize2 className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {/* Bottom-Right Minimap Overlay */}
                        <div className="absolute bottom-4 right-4 z-20 bg-[#111827]/90 border border-white/[0.08] rounded-xl p-2 shadow-lg hidden sm:block">
                            <div className="w-20 h-24 bg-black/40 rounded border border-white/[0.04] relative flex flex-col items-center justify-around py-1">
                                <div className="w-10 h-2 rounded bg-emerald-500/40" />
                                <div className="w-10 h-2 rounded bg-blue-500/40" />
                                <div className="w-10 h-2 rounded bg-blue-500/40" />
                                <div className="w-10 h-2 rounded bg-rose-500/40" />
                                <div className="w-10 h-2 rounded bg-purple-500/40" />
                                <div className="w-10 h-2 rounded bg-emerald-500/40" />
                            </div>
                        </div>

                    </div>

                </div>

            </div>

            {/* ─── Node Inspector Modal ─── */}
            {selectedNode && (
                <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-lg bg-blue-600 flex items-center justify-center text-white">
                                    <ShieldAlert className="w-3.5 h-3.5" />
                                </div>
                                <h3 className="text-[14px] font-bold text-white">
                                    Step Details: {selectedNode.title}
                                </h3>
                            </div>
                            <button onClick={() => setSelectedNode(null)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-3 text-[12px]">
                            <div className="p-3 bg-black/40 border border-white/[0.04] rounded-xl space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] text-slate-400 font-mono uppercase">Node Type</span>
                                    <span className="text-[11px] font-bold text-white font-mono uppercase px-2 py-0.5 rounded bg-white/[0.06]">
                                        {selectedNode.type}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] text-slate-400 font-mono uppercase">Target Connector</span>
                                    <span className="text-[11px] font-bold text-emerald-400 font-mono">
                                        {selectedNode.connector || "Core SOAR Engine"}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <span className="text-[11px] font-bold text-slate-400 uppercase font-mono">Description & Scope</span>
                                <p className="text-[12px] text-slate-200 bg-black/20 p-3 rounded-xl border border-white/[0.04] leading-relaxed">
                                    {selectedNode.description}
                                </p>
                            </div>

                            {selectedNode.branches && (
                                <div className="space-y-1">
                                    <span className="text-[11px] font-bold text-slate-400 uppercase font-mono">Evaluation Branches</span>
                                    <div className="space-y-1">
                                        {selectedNode.branches.map((b, idx) => (
                                            <div key={idx} className="p-2 bg-black/30 rounded-lg text-[11px] font-mono text-slate-300 flex items-center gap-2">
                                                <span className={`w-2 h-2 rounded-full ${idx === 0 ? "bg-emerald-400" : "bg-slate-500"}`} />
                                                <span>{b}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    onClick={() => setSelectedNode(null)}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Run / Test Simulation Modal ─── */}
            {runModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-xl p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-lg bg-blue-600 flex items-center justify-center text-white">
                                    <Play className="w-3 h-3 fill-current" />
                                </div>
                                <h3 className="text-[15px] font-bold text-white">
                                    Playbook Test Run: {currentWorkflow?.name}
                                </h3>
                            </div>
                            <button onClick={() => setRunModalOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {simulationRunning ? (
                            <div className="py-12 flex flex-col items-center justify-center space-y-3">
                                <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                <p className="text-[12.5px] text-slate-300 font-mono animate-pulse">
                                    Simulating multi-node orchestration execution...
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4 text-[12px]">
                                <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-300 font-mono text-[11.5px]">
                                    <span>Status: {simulationResults?.status}</span>
                                    <span>Total Execution: {simulationResults?.totalExecutionTime}</span>
                                </div>

                                <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                                    {(simulationResults?.steps || []).map((step) => (
                                        <div key={step.step} className="p-3 bg-black/40 border border-white/[0.04] rounded-xl flex items-start justify-between gap-3">
                                            <div className="space-y-0.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px] font-bold">
                                                        ✓
                                                    </span>
                                                    <span className="font-bold text-white">{step.title}</span>
                                                </div>
                                                <p className="text-[11px] text-slate-400 pl-6">{step.description}</p>
                                                <span className="text-[9.5px] font-mono text-slate-500 pl-6 block">
                                                    Via {step.connector}
                                                </span>
                                            </div>
                                            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                                                {step.latency}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                    <button
                                        onClick={() => setRunModalOpen(false)}
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                                    >
                                        Close Trace
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
